/* Connections: list, register, verify, and request a harvest.
 *
 * **The register payload now carries the credentials themselves, and the security model did not
 * change — the contract did.** A steward at a bank should not have to know this platform's internal
 * credential layout, so they type what their database actually uses; the service seals it into the
 * secret store and persists only the derived reference. Nothing comes back: `Connection` has no field
 * a credential could serialise into, so "the API never returns the credentials" is a fact about the
 * type rather than a rule somebody follows.
 *
 * Four rules hold on this side of the wire, and each answers a specific way a secret escapes a
 * browser:
 *
 * 1. **Never persisted.** The values live in the form's React state and nowhere else — no
 *    localStorage, no sessionStorage, no cookie.
 * 2. **Never in a URL.** A POST body, so nothing reaches the address bar, the browser's history, or a
 *    referrer header on the next navigation.
 * 3. **Never left in the query cache.** `register.variables` is where a submitted credential would
 *    otherwise sit — React Query keeps it on the observer until the mutation is reset, doing exactly
 *    what it should with no idea one of the variables is a password. The screen calls `reset()` once
 *    it has taken its own copy of the *outcome* (a name and a state, never a credential). Not done
 *    here in an `onSettled`, because a reset also clears `isSuccess` and `error`, which the caller
 *    needs long enough to render something.
 * 4. **Never in an error banner.** The server's refusals are host-written and echo no submitted value,
 *    which is asserted end to end; this layer adds nothing of its own to them.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { CONNECTIONS_QUERY_KEY, CONNECTIONS_URL } from '@/entities/connection';
import {
  isConnection,
  isHarvestOperation,
  isSchemaPreviewPage,
  isVerificationResult,
} from '@/shared/api/generated/catalog';
import type {
  Connection,
  ConnectorType,
  HarvestOperation,
  SchemaPreviewPage,
  VerificationResult,
} from '@/shared/api/generated/catalog';
import {
  ApiError,
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  remove,
} from '@/shared/api/http';

/* The URL and the cache key both come from `@/entities/connection`, which owns the read. These
 * mutations invalidate that exact key, so a register or a verify refreshes every screen showing
 * connections — including the dataset catalogue's grouping — rather than only this feature's own. */
const QUERY_KEY = CONNECTIONS_QUERY_KEY;

export interface ConnectionDraft {
  readonly name: string;
  readonly connector_type: ConnectorType;
  readonly host: string;
  readonly port: number;
  readonly database: string;
  /** The provider's own fields, as `/v1/catalog/connectors` says to ask for them.
   *
   * A map rather than a wide object with a nullable field per provider: five providers share three
   * field names and disagree about the rest, so a flat DTO would be a dozen optional properties whose
   * valid combinations only the server knows. The server refuses an unknown key outright, so a typo
   * here is a 422 naming the field rather than a value silently dropped.
   */
  readonly credentials: Readonly<Record<string, string>>;
}

/** One connection, selected out of the same cached read every screen already issues.
 *
 * There is no `GET /connections/{id}`. `list_connections` is the only read, capped at up to 200
 * (`Query(ge=1, le=200)`) — the same cap `useConnections` and the Connections screen already live
 * with. `SourceCard`'s own reasoning for not adding a per-connection endpoint applies here too: this
 * shares `useConnections`' exact `queryKey` and `queryFn`, so a screen that opens a connection's
 * detail page issues no request beyond the one the list already made, and two components asking
 * "which connections exist" never disagree about the answer.
 *
 * `data === undefined` after a successful read means the id was not among the fetched connections —
 * a bad id, a deleted one, or (rare, at up to 200) past the cap — never "still loading"; that is
 * `isPending`, exactly as for any other query.
 */

/* Moved to `@/entities/connection` when `catalog-browser` needed it — one feature may not import
 * another's internals, and this is a read of the same list `useConnections` already owns.
 * Re-exported so this feature's own screens read unchanged. */
export { useConnection } from '@/entities/connection';

export function useRegisterConnection(): UseMutationResult<
  Connection,
  Error,
  ConnectionDraft
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: ConnectionDraft) =>
      /* Keyed by name: a retry of "register sales-warehouse" is the same decision, while registering
       * a differently-named source is a new one. Cleared on success below, so re-using a name after
       * a deletion is not treated as a replay of the original create. */
      post(
        CONNECTIONS_URL,
        idempotencyKeyFor('register', draft.name),
        isConnection,
        'connection',
        draft,
      ),
    onSuccess: (connection) => {
      forgetIdempotencyKey('register', connection.name);
      void client.invalidateQueries({ queryKey: QUERY_KEY });
      /* **And the audit namespace, because every one of these writes an entry.** A source's own
         page renders its history from the chain, so that panel is stale the instant an action lands
         — found by replacing a credential in a browser and watching the panel keep showing two
         entries where the database had three. Invalidating the whole `audit` namespace rather than
         a hand-listed key: the front page's feed and the audit screen read the same rows and are
         equally stale, and a listed set is one somebody forgets to extend — the defect §22 records
         for the score listing, where a prefix mismatch meant the invalidation never reached it. */
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
    retry: false,
  });
}

/* Verification returns 200 whether or not the source answered.
 *
 * `reachable: false` is a *result*, not an error — the request was processed exactly as asked and the
 * source being down is the answer. So this mutation succeeds in both cases and the screen reads
 * `reachable`. Treating it as an error would also have hidden the useful half: the server records
 * `state: failed`, and that state is in the response.
 */
export function useVerifyConnection(): UseMutationResult<
  VerificationResult,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      post(
        `${CONNECTIONS_URL}/${encodeURIComponent(connectionId)}/verify`,
        idempotencyKeyFor('verify', connectionId),
        isVerificationResult,
        'verification result',
        undefined,
      ),
    /* `onSettled`, not `onSuccess`: an unreachable source still moved the stored state to `failed`,
     * so the list is stale either way. Refetching only on success would leave the row reading
     * `registered` while the server records something else. */
    onSettled: (_data, _error, connectionId) => {
      forgetIdempotencyKey('verify', connectionId);
      void client.invalidateQueries({ queryKey: QUERY_KEY });
      /* **And the audit namespace, because every one of these writes an entry.** A source's own
         page renders its history from the chain, so that panel is stale the instant an action lands
         — found by replacing a credential in a browser and watching the panel keep showing two
         entries where the database had three. Invalidating the whole `audit` namespace rather than
         a hand-listed key: the front page's feed and the audit screen read the same rows and are
         equally stale, and a listed set is one somebody forgets to extend — the defect §22 records
         for the score listing, where a prefix mismatch meant the invalidation never reached it. */
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
    retry: false,
  });
}

/* What a harvest would find, before anybody commits to one.
 *
 * **`enabled` is the whole point of this hook's shape.** It reaches the customer's source, so it must
 * fire only when a reader has actually asked — a query that ran on mount would dial a production
 * database every time the wizard rendered. `null` means "not asked yet".
 *
 * `retry: false`, unlike the reads that hit our own API: a source that refused a listing will refuse
 * the next two as well, and three attempts against a customer's database to render one error is
 * source load hard rule 14 exists to bound.
 */
export function useSchemaPreview(
  connectionId: string | null,
): UseQueryResult<SchemaPreviewPage> {
  return useQuery({
    queryKey: ['catalog', 'connections', connectionId, 'schemas'],
    enabled: connectionId !== null,
    retry: false,
    queryFn: () =>
      getOne(
        `/v1/catalog/connections/${String(connectionId)}/schemas`,
        isSchemaPreviewPage,
        'schema preview',
      ),
  });
}

/* Retire a source.
 *
 * **Invalidates the connections list on success rather than removing the row locally.** An optimistic
 * removal would be wrong here in a way it usually is not: the server refuses with a 409 when the
 * source still has datasets, so the delete a reader initiates is one the server may decline — and a
 * row that vanished and came back is worse than one that never moved.
 *
 * `remove` rather than a `post` with a body: the operation has no payload, and the platform's DELETE
 * takes no idempotency key because a DELETE cannot double.
 */
export interface DeleteConnectionRequest {
  readonly connectionId: string;
  /** How many datasets to retire with the source, or `undefined` to refuse if it has any.
   *
   * **The count, not a flag.** The server checks it against the source's current dataset count and
   * refuses if they differ, which is what stops a dialog minutes old retiring tables a harvest
   * added in the meantime. So this is only ever the number the server itself just reported.
   */
  readonly retireDatasets?: number;
}

export function useDeleteConnection(): UseMutationResult<
  void,
  Error,
  DeleteConnectionRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, retireDatasets }: DeleteConnectionRequest) =>
      remove(
        `/v1/catalog/connections/${encodeURIComponent(connectionId)}` +
          (retireDatasets === undefined
            ? ''
            : `?retire_datasets=${String(retireDatasets)}`),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY });
      /* The catalogue too, because a cascade retires datasets: leaving that cache alone would show
         a steward tables belonging to a source that no longer exists. */
      void client.invalidateQueries({ queryKey: ['catalog'] });
      /* **And the audit namespace, because every one of these writes an entry.** A source's own
         page renders its history from the chain, so that panel is stale the instant an action lands
         — found by replacing a credential in a browser and watching the panel keep showing two
         entries where the database had three. Invalidating the whole `audit` namespace rather than
         a hand-listed key: the front page's feed and the audit screen read the same rows and are
         equally stale, and a listed set is one somebody forgets to extend — the defect §22 records
         for the score listing, where a prefix mismatch meant the invalidation never reached it. */
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/** How many datasets a 409 says the source still has, or `null` if it was not that refusal.
 *
 * Read from the problem document's extension member rather than parsed out of the sentence, so the
 * wording stays free to change and a translation cannot break the button. `null` for every other
 * refusal — a stale-count 409 deliberately does *not* carry one, because offering to retry with a
 * number the server has just called wrong would walk the operator into the same wall.
 */
export function datasetsBlockingDelete(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const datasets = error.extras['datasets'];
  return typeof datasets === 'number' && Number.isInteger(datasets) && datasets > 0
    ? datasets
    : null;
}

/* Taking a source out of service, and putting it back.
 *
 * **One hook covering both directions**, because the two are one decision reversed: whoever silences
 * a source un-silences it, the server authorizes both under one action, and a screen renders exactly
 * one of the two buttons at a time. Two hooks would be two cache-invalidation stories for one row.
 *
 * **The URL says the direction rather than a body flag.** That is the server's shape and the reason is
 * worth keeping on this side too: a client that computed the wrong boolean would silently take a
 * source out of service, where a client that built the wrong URL gets a 404.
 *
 * `onSuccess`, not `onSettled`: unlike `verify`, a refusal here changes nothing — a 409 means the
 * source was already in the state asked for — so there is no stale row to refetch. Invalidating on a
 * refusal would hide the refusal behind a re-render.
 */
export interface ServiceChange {
  readonly connectionId: string;
  /** `true` to put the source back in service, `false` to take it out. */
  readonly inService: boolean;
}

export function useSetInService(): UseMutationResult<Connection, Error, ServiceChange> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, inService }: ServiceChange) =>
      post(
        `${CONNECTIONS_URL}/${encodeURIComponent(connectionId)}/` +
          (inService ? 'enable' : 'disable'),
        /* Keyed on the direction as well as the source. Without it, disabling and then re-enabling
         * within one session would replay the first key and the second press would be treated as a
         * duplicate of the first — which is the `SubmissionGuard` trap the harvest hook already
         * forgets its key to avoid. */
        idempotencyKeyFor(inService ? 'enable' : 'disable', connectionId),
        isConnection,
        'connection',
        undefined,
      ),
    onSuccess: (_connection, { connectionId, inService }) => {
      forgetIdempotencyKey(inService ? 'enable' : 'disable', connectionId);
      void client.invalidateQueries({ queryKey: QUERY_KEY });
      /* **And the audit namespace, because every one of these writes an entry.** A source's own
         page renders its history from the chain, so that panel is stale the instant an action lands
         — found by replacing a credential in a browser and watching the panel keep showing two
         entries where the database had three. Invalidating the whole `audit` namespace rather than
         a hand-listed key: the front page's feed and the audit screen read the same rows and are
         equally stale, and a listed set is one somebody forgets to extend — the defect §22 records
         for the score listing, where a prefix mismatch meant the invalidation never reached it. */
      void client.invalidateQueries({ queryKey: ['audit'] });
      /* The catalogue too. A disabled source stops being harvested and profiled, so its summary row
       * — which carries the harvest state the connections table renders — is stale the moment this
       * lands. */
      void client.invalidateQueries({ queryKey: ['catalog'] });
    },
    retry: false,
  });
}

/* Replacing the credential behind a source.
 *
 * **The one mutation in this feature that carries credential material after registration**, and every
 * rule `useRegisterConnection` holds applies here unchanged: never persisted, never in a URL (a POST
 * body), never left in the query cache (`reset()` after the caller has taken its own copy of the
 * outcome), and never in an error banner, because the server's refusals are host-written and echo no
 * submitted value.
 *
 * **No `connector_type` in the payload.** The server reads it from the stored connection, so this
 * cannot change what kind of source it is and a provider mismatch comes back as a 422 naming both
 * types rather than as material stored against the wrong source.
 *
 * `retry: false` matters more here than elsewhere: a retried credential write is a second seal of the
 * same material at the same path, which is harmless, and a retried *rejected* one is two 422s about a
 * password a reader has to retype either way.
 */
export interface CredentialReplacement {
  readonly connectionId: string;
  readonly credentials: Readonly<Record<string, string>>;
}

export function useReplaceCredential(): UseMutationResult<
  Connection,
  Error,
  CredentialReplacement
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ connectionId, credentials }: CredentialReplacement) =>
      post(
        `${CONNECTIONS_URL}/${encodeURIComponent(connectionId)}/credential`,
        idempotencyKeyFor('credential', connectionId),
        isConnection,
        'connection',
        { credentials },
      ),
    onSuccess: (_connection, { connectionId }) => {
      /* Forgotten so a second replacement later in the session is a new request rather than a replay
       * that returns the first one — which for a credential would mean an operator believing they
       * had rotated a password that was never sent. */
      forgetIdempotencyKey('credential', connectionId);
      void client.invalidateQueries({ queryKey: QUERY_KEY });
      /* **And the audit namespace, because every one of these writes an entry.** A source's own
         page renders its history from the chain, so that panel is stale the instant an action lands
         — found by replacing a credential in a browser and watching the panel keep showing two
         entries where the database had three. Invalidating the whole `audit` namespace rather than
         a hand-listed key: the front page's feed and the audit screen read the same rows and are
         equally stale, and a listed set is one somebody forgets to extend — the defect §22 records
         for the score listing, where a prefix mismatch meant the invalidation never reached it. */
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
    retry: false,
  });
}

export function useRequestHarvest(): UseMutationResult<HarvestOperation, Error, string> {
  return useMutation({
    mutationFn: (connectionId: string) =>
      post(
        `${CONNECTIONS_URL}/${encodeURIComponent(connectionId)}/harvest`,
        idempotencyKeyFor('harvest', connectionId),
        isHarvestOperation,
        'harvest operation',
        undefined,
      ),
    onSettled: (_data, _error, connectionId) => {
      /* Forgotten so a *second* harvest of the same source later in the session is a new request
       * rather than a replay that returns the first operation. */
      forgetIdempotencyKey('harvest', connectionId);
    },
    retry: false,
  });
}

/** Polls one harvest until it stops moving.
 *
 * Polling rather than a websocket because a harvest is minutes long and this is one row on one
 * screen — a persistent connection per operator watching a harvest is a lot of infrastructure for a
 * progress bar. The interval stops on a terminal state so a forgotten tab is not a permanent load.
 */
export function useHarvest(harvestId: string | null): UseQueryResult<HarvestOperation> {
  return useQuery({
    queryKey: ['catalog', 'harvest', harvestId],
    enabled: harvestId !== null,
    queryFn: () =>
      getOne(
        `/v1/catalog/harvests/${encodeURIComponent(harvestId ?? '')}`,
        isHarvestOperation,
        'harvest operation',
      ),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state === undefined) return 2000;
      /* Polling stops dead on a terminal state. That is what bounds this: a forgotten tab holding a
       * finished harvest costs nothing, and there is nothing left to learn. */
      return state === 'pending' || state === 'running' ? 2000 : false;
    },
    /* Keeps polling while the tab is in the background, which TanStack otherwise pauses.
     *
     * Needed because the app sets `refetchOnWindowFocus: false` globally — deliberately, so an
     * operator alt-tabbing does not trigger a storm of refetches. Together those two defaults meant
     * a user who switched away mid-harvest came back to a panel frozen at "Running" for ever, with
     * the harvest long since finished. Safe to leave on here precisely because the interval stops
     * itself: this is the one query in the product whose polling has a natural end. */
    refetchIntervalInBackground: true,
    retry: false,
  });
}
